import React, { useState, useEffect } from 'react'
import { ModulePanel, ModuleHeader, ModuleBody, ModuleFooter } from 'containers/purchase/modules/module_panel'
import { Row, Col,  Button, ListGroup, Nav, NavItem } from 'reactstrap'
import ListItemOption from 'ui/list_item_option'
import { useTranslation } from 'react-i18next'
import { currency } from 'i18n/config'
import { get } from 'lodash'
import parse from 'react-html-parser'
import cn from 'classnames'
import resCn from 'lib/responsive_classes'

const LanguageFilter = ({ languages, languageFilter, setLanguageFilter }) => {
  if (!languages) return ''

  return (
    <Nav fill pills justified>
      {languages.map(({ id, name }, i) => (
        <NavItem
          key={id}
          onClick={() => setLanguageFilter(id)}
          className={cn([
            languageFilter === id ? ['active', 'bg-lighter'] : 'bg-lighter',
            'd-flex',
            'justify-content-center',
            'rounded',
            'text-uppercase',
            'mt-2',
            i === languages.length - 1 ? 'me-0' : 'me-0 me-md-3',
            'mb-4'
          ])}
          style={{
            cursor: 'pointer',
            border: '1px solid var(--bs-dark)'
          }}
        >
          <div
            className={cn([
              'p-2',
              languageFilter === id ? 'bg-primary' : 'bg-light',
              'd-flex'
            ])}
          >
            <i
              className={cn([
                languageFilter === id
                  ? 'fa-dot-circle text-white'
                  : 'fa-circle text-white',
                'p-2 align-self-center fa'
              ])}
            />
          </div>
          <div className="w-100 p-2 d-flex justify-content-center align-self-center">
            {name}
          </div>
        </NavItem>
      ))}
    </Nav>
  )
}

const ComponentSelect = ({ components, component, onComponentSelected, resSize, renderType }) => {
  const { t, i18n } = useTranslation()

  if (!components || components.length <= 0) {
    return (
      <div className="alert alert-warning">
        {i18n.t('tourcms_component_module.no_availability')}
      </div>
    )
  }

  if (renderType === 'list') {
    return (
      <ListGroup>
        {components.map((c) => {
          const {
            component_key,
            date_code,
            note,
            special_offer_note,
            total_price,
            price_breakdown
          } = c

          const price = get(price_breakdown, 'price_row')

          const priceList = Array.isArray(price) ? price : [price]

          const componentDescription = (date_code, note, special_offer_note) => {
            let text
            let dateText = date_code

            if (date_code && date_code.includes('NOTSET-NOTSET')) {
              dateText = null
            }

            if (dateText || note) {
              if (dateText && note) text = `${dateText} - ${note}`
              else if (!dateText) text = note
              else text = dateText
            } else {
              text = special_offer_note
            }

            return <div>{text}</div>
          }

          return (
            <ListItemOption
              key={component_key}
              active={component.component_key === component_key}
              onClick={() => onComponentSelected(c)}
              resSize={resSize}
            >
              <div 
                className={resCn({
                  slim: "text-center",
                  wide: "text-md-start",
                  all: "align-self-center"
                }, resSize)}              
              >
                <div className="fw-bold">
                  {componentDescription(date_code, note, special_offer_note)}
                </div>
                <div>
                  {priceList.map((p) => (
                    <span key={p['rate-id']} className="me-3">
                      {parse(p.text)}
                    </span>
                  ))}
                </div>
              </div>
              <div className="d-flex align-self-center text-end">
                <div
                  className={resCn({
                    slim: "text-center mt-2",
                    wide: "text-md-end mt-md-0",
                    all: "price fw-bold"
                  }, resSize)}
                  style={{ fontSize: '18px', width: '200px' }}
                >
                  {i18n.t('tourcms_component_module.total_price', {
                    price: currency(total_price)
                  })}
                </div>
              </div>
            </ListItemOption>
          )
        })}
      </ListGroup>
    )    
  }  

  if (renderType === 'pills') {
    return (
      <Row className="row-cols-2">
        {components.map((c) => {
          const {
            component_key,
            date_code,
            total_price
          } = c

          return (
            <Col className='mt-2' key={component_key}>
              <Button 
                color="primary"
                className='w-100 d-flex justify-content-between'
                key={component_key}
                active={component.component_key === component_key}
                onClick={() => onComponentSelected(c)}      
              >
                <i
                  className={cn([
                    component.component_key === component_key
                      ? 'fa-dot-circle text-white'
                      : 'fa-circle text-white',
                    'p-2 align-self-center fa'
                  ])}
                />              
                <div class="text-white d-flex flex-column w-100">
                  <div className="fw-bold">
                    <i class="far fa-clock me-1" aria-hidden="true"/>
                    {date_code}
                  </div>
                  <div className="d-flex justify-content-center">
                    <div className="badge bg-white text-primary mt-1 w-50">{currency(total_price)}</div> 
                  </div>              
                </div>            
              </Button>          
            </Col>
          )
        })}
      </Row>
    )
  } 
}

const TourcmsComponentModule = (props) => {
  const { machine, config, status, resSize} = props
  const { t, i18n } = useTranslation()
  const [current, send] = machine
  const { components, component } = current.context
  const { external_api_config } = config
  const componentsList = Array.isArray(components) ? components : [components]
  const [languages, setLanguages] = useState([])
  const supplier_note_group =
    external_api_config && external_api_config.supplier_note_group
  const renderType = external_api_config && (external_api_config.render_type || 'list')
  const [languageFilter, setLanguageFilter] = useState(
    languages[0] ? languages[0].id : null
  )

  useEffect(() => {
    if (supplier_note_group === 'language' && componentsList.length > 0) {
      setLanguages(componentsList[0].languages)
      setLanguageFilter(componentsList[0].language.id)
    }
  }, [components])

  const isFetching = current.matches({
    tourcmsComponentStep: 'fetchingComponents'
  })

  const onComponentSelected = (component) =>
    send({ type: 'COMPONENT_SELECTION', component })

  let filteredComponents = componentsList
  if (languageFilter) {
    filteredComponents = filteredComponents.filter(
      ({ language }) => language.id === languageFilter
    )
  }

  useEffect(() => {
    if (filteredComponents.length > 0)
      onComponentSelected(filteredComponents[0])
  }, [languageFilter])

  return (
    <ModulePanel>
      <ModuleHeader
        status={status}
        onClick={() =>
          status === 'completed' ? send('REOPEN_TOURCMSCOMPONENTSTEP') : null
        }
      >
        <span>
          <i className="fa fa-star me-2" aria-hidden="true"></i>
          {i18n.t('tourcms_component_module.select_a_modality')}
        </span>
      </ModuleHeader>
      <ModuleBody status={status}>
        {isFetching ? (
          <div className="alert alert-light" role="alert">
            <i className="far fa-clock me-2" aria-hidden="true"></i>
            {i18n.t('tourcms_component_module.searching_modalities')}
          </div>
        ) : (
          <>
            <LanguageFilter
              languages={languages}
              languageFilter={languageFilter}
              setLanguageFilter={setLanguageFilter}
            />
            {components ? (
              <ComponentSelect
                components={filteredComponents}
                component={component}
                onComponentSelected={onComponentSelected}
                resSize={resSize}
                renderType={renderType}
              />
            ) : (
              <div className="alert alert-light" role="alert">
                <i className="fa fa-warning me-2" aria-hidden="true"></i>
                {i18n.t('tourcms_component_module.no_components')}
              </div>
            )}
          </>
        )}
      </ModuleBody>
      <ModuleFooter {...props} />
    </ModulePanel>
  )
}

export default TourcmsComponentModule
